// Third party
import moment from "moment";

// Global Constants
import { ID_NEW_SETUP } from "@/Motivators/constants";
import { canUserEditMotivators } from "@/Motivators/helpers";

// Helpers
import { or } from "@/Common/helpers/sonar";
import { DATE_FORMAT } from "@/Constants/date";

const statuses = ["CLOSING", "ACTIVE", "POSTED", "STAGING"];
const autoAlertsStatuses = ["ACTIVE", "STAGING"];

const ERROR_MESSAGE = "Error in generate tree function";

/*******************************************************************************************************************************/
/************************************************** TREE GENERATION FUNCTIONS **************************************************/
/*******************************************************************************************************************************/

const getOriginalCommissionCycle = (commissionCycles, commissionCycleObject, spiffId) => {
	if (!spiffId) {
		return {};
	}
	let originalCommissionCycleId = null;
	let parentCommissionCycleId = null;

	if (spiffId === ID_NEW_SETUP) {
		return { originalCommissionCycleId, parentCommissionCycleId };
	}

	const filteredCycles = commissionCycles.filter((cycle) => cycle.spiffs.some((setup) => setup._id === spiffId));

	if (filteredCycles?.length && filteredCycles?.length > 1) {
		const firstElTitle = filteredCycles[0].title;

		// avoiding the first element/parent from the loop
		if (firstElTitle !== commissionCycleObject?.title) {
			originalCommissionCycleId = commissionCycleObject?._id;
			parentCommissionCycleId = filteredCycles[0]._id;
		}
	}

	return { originalCommissionCycleId, parentCommissionCycleId };
};

const isDateInThePast = (date) => moment(date).isBefore(moment());

export const generateCommissionCycleTree2Levels = function ({
	data: commissionCyclesInput = [],
	locationId = "",
	hasPermission: auth = false,
	masterEdit = { SALES: false, SERVICE: false },
	motivatorType,
}) {
	const validStatuses = ["CLOSING", "ACTIVE", "POSTED", "STAGING"];
	if (!commissionCyclesInput?.length) {
		return [];
	}

	// Work with a copy of commissionCycles to avoid mutating the input
	const commissionCycles = commissionCyclesInput.map((cycle) => ({ ...cycle, spiffs: [...cycle.spiffs] }));

	// Sort commissionCycles without causing side effects
	commissionCycles.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));

	const mapResults = new Map();
	let expanded = [];

	commissionCycles.forEach((commissionCycleObject) => {
		const hasNewSpiff = commissionCycleObject.spiffs.some((spiff) => spiff._id === ID_NEW_SETUP);
		const year = moment(commissionCycleObject.startDate).utc().format(DATE_FORMAT.yearOnly);
		const month = moment(commissionCycleObject.startDate).utc().format(DATE_FORMAT.monthLong);

		if (
			(validStatuses.some((s) => commissionCycleObject.status === s) && !isDateInThePast(commissionCycleObject.endDate)) ||
			canUserEditMotivators(masterEdit[motivatorType] ?? false, commissionCycleObject.status)
		) {
			expanded.push(year);
			expanded.push(commissionCycleObject._id);
			commissionCycleObject.spiffs.push({
				title: "Add New Setup",
				controlled: true,
				disabled: or(!auth, hasNewSpiff),
				_id: `${commissionCycleObject._id}`,
			});
		}

		let innerList = {
			_id: year,
			title: year,
			bold: true,
			list: [],
		};

		if (mapResults.get(year)) {
			innerList = mapResults.get(year);
		}

		const spiffSetups = [];
		try {
			commissionCycleObject?.spiffs?.forEach((element) => {
				if (element) {
					const { originalCommissionCycleId, parentCommissionCycleId } = getOriginalCommissionCycle(
						commissionCycles,
						commissionCycleObject,
						element._id
					);

					spiffSetups.push({
						locationId,
						parentCommissionCycleId,
						originalCommissionCycleId,
						_id: `${originalCommissionCycleId}_${element._id}`,
						title: element?.title ?? "",
						commissionCycleId: commissionCycleObject._id,
						commissionCycleStartDate: commissionCycleObject.startDate,
						commissionCycleEndDate: commissionCycleObject.endDate,
						badge: {
							title: Number(element.itemsCount),
							variant: "numeric",
						},
						controlled: element?.controlled ?? false,
						disabled: element?.disabled,
						effectiveDateFrom: element.effectiveDateFrom,
						effectiveDateTo: element.effectiveDateTo,
						status: commissionCycleObject?.status,
						lastModifiedBy: element.lastModifiedBy,
						lastModifiedOn: element.lastModifiedOn,
						createdBy: element.createdBy,
						createdAt: element.createdAt,
					});
				}
			});
		} catch (error) {
			// Handle error silently or ensure the error handling does not produce side effects
		}

		innerList.list.push({
			_id: commissionCycleObject._id,
			title: month,
			disabled: !spiffSetups?.length,
			list: spiffSetups,
		});

		mapResults.set(year, innerList);
	});

	expanded = [...new Set(expanded)];

	return Array.from(mapResults.values());
};

// GENERATE TREE FOR TRAINING
export const generateTrainingCommissionCycleTree = ({ data: commissionCyclesInput = [] }) => {
	const commissionCycles = commissionCyclesInput.map((cycle) => ({ ...cycle, ...cycle.training }));
	// Sort commissionCycles without causing side effects
	commissionCycles.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));

	const mapResults = new Map();
	let expanded = [];

	commissionCycles.forEach((mainElement) => {
		const year = moment(mainElement.startDate).utc().format(DATE_FORMAT.yearOnly);
		const month = moment(mainElement.startDate).utc().format(DATE_FORMAT.monthLong);

		if (mainElement.status === "CLOSING" || mainElement.status === "ACTIVE" || mainElement.status === "POSTED") {
			expanded.push(year);
			expanded.push(mainElement._id);
			mainElement["active"] = true;
		} else {
			mainElement["active"] = false;
		}

		let innerList = {
			_id: year,
			title: year,
			bold: true,
			list: [],
		};

		if (mapResults.get(year)) {
			innerList = mapResults.get(year);
		}

		innerList.list.push({
			_id: mainElement._id,
			commissionCycleId: mainElement._id,
			title: month,
			badge: {
				title: mainElement.itemsCount,
				variant: "numeric",
			},
			status: mainElement.status,
			effectiveDateRangeFrom: mainElement.startDate,
			effectiveDateRangeTo: mainElement.endDate,
		});

		mapResults.set(year, innerList);
	});

	expanded = [...new Set(expanded)];

	return Array.from(mapResults.values());
};

export const generateAutoAlertsSetupTree = ({
	data: commissionCyclesInput,
	locationId,
	hasPermission: auth,
	masterEdit = { SALES: false, SERVICE: false },
}) => {
	if (!commissionCyclesInput) {
		return [];
	}

	// Work with a copy of commissionCycles to avoid mutating the input
	const commissionCycles = commissionCyclesInput.map((cycle) => ({ ...cycle, autoAlertsSetups: [...cycle.autoAlertsSetups] }));

	commissionCycles.sort((a, b) => new Date(a.startDate.substr(0, 10)) - new Date(b.startDate.substr(0, 10)));

	const mapResults = new Map();
	let expanded = [];

	commissionCycles?.forEach((mainElement) => {
		const hasNewAuto = mainElement.autoAlertsSetups.some((auto) => auto.autoAlertsSetupId._id === ID_NEW_SETUP);
		const year = moment(mainElement.startDate).utc().format(DATE_FORMAT.yearOnly);
		const month = moment(mainElement.startDate).utc().format(DATE_FORMAT.monthLong);

		if (
			(autoAlertsStatuses.includes(mainElement.status) && !isDateInThePast(mainElement.endDate)) ||
			canUserEditMotivators(masterEdit.SALES, mainElement.status)
		) {
			expanded.push(year);
			expanded.push(mainElement._id);
			mainElement.autoAlertsSetups.push({
				autoAlertsSetupId: {
					title: "Add New Setup",
					controlled: true,
					disabled: !auth || hasNewAuto,
					_id: `${mainElement._id}`,
				},
			});
		}

		let innerList = {
			_id: year,
			title: year,
			bold: true,
			list: [],
		};

		if (mapResults.get(year)) {
			innerList = mapResults.get(year);
		}

		const autoAlertsSetups = [];

		try {
			mainElement?.autoAlertsSetups?.forEach((element) => {
				if (element?.autoAlertsSetupId) {
					const { originalCommissionCycleId, parentCommissionCycleId } = getOriginalCommissionCycleTest(
						commissionCycles,
						mainElement,
						element.autoAlertsSetupId._id
					);

					autoAlertsSetups.push({
						locationId,
						parentCommissionCycleId,
						originalCommissionCycleId,
						_id: `${originalCommissionCycleId}_${element.autoAlertsSetupId._id}`,
						title: element.autoAlertsSetupId.title || "",
						commissionCycleId: mainElement._id,
						status: mainElement.status,
						commissionCycleStartDate: mainElement.startDate,
						autoAlertsSetupId: element.autoAlertsSetupId._id,
						badge: {
							title: element.autoAlertsSetupId.itemsCount,
							variant: "numeric",
						},
						controlled: element?.autoAlertsSetupId?.controlled || false,
						disabled: element.autoAlertsSetupId?.disabled,
						effectiveDateFrom: element.autoAlertsSetupId?.effectiveDateFrom,
						effectiveDateTo: element.autoAlertsSetupId?.effectiveDateTo,
						createdBy: element.autoAlertsSetupId.createdBy,
						createdOn: element.autoAlertsSetupId.createdOn,
						lastModifiedBy: element.autoAlertsSetupId.lastModifiedBy,
						lastModifiedOn: element.autoAlertsSetupId.lastModifiedOn,
					});
				}
			});
		} catch (error) {
			console.log(ERROR_MESSAGE, error);
		}

		innerList.list.push({
			_id: mainElement._id,
			title: month,
			disabled: !autoAlertsSetups?.length,
			list: autoAlertsSetups,
		});

		mapResults.set(year, innerList);
	});

	expanded = [...new Set(expanded)];

	return Array.from(mapResults.values());
};

const getOriginalCommissionCycleTest = (commissionCycles, mainElement, autoAlertsSetupId) => {
	if (!autoAlertsSetupId) {
		return {};
	}
	let originalCommissionCycleId = null;
	let parentCommissionCycleId = null;

	const filteredCycles = commissionCycles.filter((cycle) =>
		cycle.autoAlertsSetups.some((setup) => setup.autoAlertsSetupId._id === autoAlertsSetupId)
	);

	if (filteredCycles?.length && filteredCycles?.length > 1) {
		const firstElTitle = filteredCycles[0].title;

		// avoiding the first element/parent from the loop
		if (firstElTitle !== mainElement?.title) {
			originalCommissionCycleId = mainElement?._id;
			parentCommissionCycleId = filteredCycles[0]._id;
		}
	}

	return { originalCommissionCycleId, parentCommissionCycleId };
};

export const generateSpecialUnitsListTree = ({ data: commissionCyclesInput, locationId, hasPermission: auth }) => {
	const listProp = "specialUnitsList";
	const idProp = "specialUnitsListId";
	// Order lists
	if (!commissionCyclesInput) {
		return [];
	}

	const commissionCycles = commissionCyclesInput?.map((cycle) => ({ ...cycle, [listProp]: [...(cycle[listProp] || [])] }));

	commissionCycles.sort((a, b) => new Date(a.startDate.substr(0, 10)) - new Date(b.startDate.substr(0, 10)));

	const mapResults = new Map();
	let expanded = [];

	commissionCycles.forEach((mainElement) => {
		const hasNewItem = mainElement.specialUnitsList.some((value) => value.specialUnitsListId._id === ID_NEW_SETUP);
		const year = moment(mainElement.startDate).utc().format(DATE_FORMAT.yearOnly);
		const month = moment(mainElement.startDate).utc().format(DATE_FORMAT.monthLong);

		if (statuses.includes(mainElement.status)) {
			expanded.push(year);
			expanded.push(mainElement._id);

			// mainElement.specialUnitsLists.push({
			mainElement.specialUnitsList.push({
				specialUnitsListId: {
					title: "Add New List",
					controlled: true,
					disabled: or(!auth, hasNewItem),
					_id: `${mainElement._id}`,
				},
			});
		}

		let innerList = {
			_id: year,
			title: year,
			bold: true,
			list: [],
		};

		if (mapResults.get(year)) {
			innerList = mapResults.get(year);
		}

		const specialUnitsList = [];

		try {
			mainElement?.specialUnitsList?.forEach((element) => {
				if (element?.specialUnitsListId) {
					const { originalCommissionCycleId, parentCommissionCycleId } = getOriginalCommissionCycleSpecialUnits({
						commissionCycles,
						mainElement,
						specialUnitsListId: element?.specialUnitsListId?._id,
					});
					specialUnitsList.push({
						locationId,
						parentCommissionCycleId,
						originalCommissionCycleId,
						_id: `${originalCommissionCycleId}_${element.specialUnitsListId._id}`,
						title: element.specialUnitsListId.title ?? "",
						commissionCycleId: mainElement._id,
						[idProp]: element.specialUnitsListId._id,
						badge: {
							title: element.specialUnitsListId.unitsCount,
							variant: "numeric",
						},
						controlled: element?.specialUnitsListId?.controlled ?? false,
						specialUnitsListType: element?.specialUnitsListId?.specialUnitsListType,
						// new fields
						commissionCycleStartDate: mainElement.startDate,
						commissionCycleEndDate: mainElement.endDate,
						effectiveDateFrom: element[idProp].effectiveDateFrom,
						effectiveDateTo: element[idProp].effectiveDateTo,
						status: mainElement?.status,
						disabled: element[idProp].disabled,
						createdBy: element[idProp]?.createdBy ?? {},
						createdOn: element[idProp]?.createdAt,
						lastModifiedBy: element[idProp]?.lastModifiedBy ?? {},
						lastModifiedOn: element[idProp]?.lastModifiedOn,
					});
				}
			});
		} catch (error) {
			console.log(ERROR_MESSAGE, error);
		}

		innerList.list.push({
			_id: mainElement._id,
			title: month,
			disabled: !specialUnitsList?.length,
			list: specialUnitsList,
		});

		mapResults.set(year, innerList);
	});

	expanded = [...new Set(expanded)];

	return Array.from(mapResults.values());
};

const getOriginalCommissionCycleSpecialUnits = ({ commissionCycles, mainElement, specialUnitsListId }) => {
	let originalCommissionCycleId = null;
	let parentCommissionCycleId = null;

	if (specialUnitsListId) {
		const filteredCycles = commissionCycles.filter((cycle) =>
			cycle.specialUnitsList.some((setup) => {
				if (setup?.specialUnitsListId?._id) {
					return setup.specialUnitsListId._id === specialUnitsListId;
				} else {
					return false;
				}
			})
		);

		if (filteredCycles?.length && filteredCycles?.length > 1) {
			const firstElTitle = filteredCycles[0].title;
			// avoiding the first element/parent from the loop
			if (firstElTitle !== mainElement?.title) {
				originalCommissionCycleId = mainElement?._id;
				parentCommissionCycleId = filteredCycles[0]._id;
			}
		}
	}

	return { originalCommissionCycleId, parentCommissionCycleId };
};

export const generateCommissionCycleTreeTempGuaranteed = function ({
	data: commissionCyclesInput,
	locationId,
	hasPermission: auth,
	masterEdit = { SALES: false, SERVICE: false },
}) {
	const listProp = "temporaryGuaranties";
	const idProp = "temporaryGuaranteedId";
	const linkTitle = "Add New List";

	if (commissionCyclesInput) {
		const commissionCycles = commissionCyclesInput.map((cycle) => ({ ...cycle, [listProp]: [...cycle[listProp]] }));

		commissionCycles.sort((a, b) => new Date(a.startDate.substr(0, 10)) - new Date(b.startDate.substr(0, 10)));

		const mapResults = new Map();
		let expanded = [];

		commissionCycles?.forEach((mainElement) => {
			const hasNewTemp = mainElement[listProp].some((temp) => temp.temporaryGuaranteedId._id === ID_NEW_SETUP);
			const year = moment(mainElement.startDate).utc().format(DATE_FORMAT.yearOnly);
			const month = moment(mainElement.startDate).utc().format(DATE_FORMAT.monthLong);

			if (
				(statuses.includes(mainElement.status) && !isDateInThePast(mainElement.endDate)) ||
				canUserEditMotivators(masterEdit.SERVICE, mainElement.status)
			) {
				expanded.push(year);
				expanded.push(mainElement._id);
				mainElement[listProp].push({
					[idProp]: {
						title: linkTitle,
						controlled: true,
						disabled: !auth || hasNewTemp,
						_id: `${mainElement._id}`,
					},
				});
			}

			let innerList = {
				_id: year,
				title: year,
				bold: true,
				list: [],
			};

			if (mapResults.get(year)) {
				innerList = mapResults.get(year);
			}

			const generalList = [];

			const processedList = processElements({ mainElement, listProp, idProp, locationId, commissionCycles, generalList });

			innerList.list.push({
				_id: mainElement._id,
				title: month,
				disabled: !processedList?.length,
				list: processedList,
			});

			mapResults.set(year, innerList);
		});

		expanded = [...new Set(expanded)];

		return Array.from(mapResults.values());
	}

	return null;
};

function processElements({ mainElement, listProp, idProp, locationId, commissionCycles, generalList }) {
	try {
		mainElement[listProp]?.forEach((element) => {
			if (element[idProp]) {
				const { originalCommissionCycleId, parentCommissionCycleId } = getOriginalCommissionCycleTemp({
					commissionCycles,
					mainElement,
					listProp,
					idProp,
					elementId: element[idProp]._id,
				});

				generalList.push({
					locationId,
					parentCommissionCycleId,
					originalCommissionCycleId,
					_id: `${originalCommissionCycleId}_${element[idProp]._id}`,
					title: element[idProp].title || "",
					commissionCycleId: mainElement._id,
					createdBy: element[idProp]?.createdBy || {},
					createdOn: element[idProp]?.createdOn,
					lastModifiedBy: element[idProp]?.lastModifiedBy || {},
					lastModifiedOn: element[idProp]?.lastModifiedOn,
					commissionCycleStartDate: mainElement.startDate,
					commissionCycleEndDate: mainElement.endDate,
					effectiveDateFrom: element[idProp].effectiveDateFrom,
					effectiveDateTo: element[idProp].effectiveDateTo,
					[idProp]: element[idProp]._id,
					badge: {
						title: element[idProp].itemsCount,
						variant: "numeric",
					},
					controlled: element[idProp]?.controlled || false,
					status: mainElement?.status,
					commissionCycleStatus: mainElement?.status,
					disabled: element[idProp].disabled,
				});
			}
		});
	} catch (error) {
		console.log(ERROR_MESSAGE, error);
	}

	return generalList;
}

export const filteredRowsByCommissionCyle = ({ _parentId, ccStartDate, ccEndDate, rows }) => {
	if (!rows?.length) {
		return rows;
	}
	if (!ccStartDate || !ccEndDate) {
		return rows;
	}

	if (typeof ccStartDate === "string") {
		ccStartDate = moment.utc(ccStartDate).startOf("month");
	} else if (moment.isMoment(ccStartDate)) {
		ccStartDate = moment.utc(ccStartDate.format()).startOf("month");
	}

	if (typeof ccEndDate === "string") {
		ccEndDate = moment.utc(ccEndDate).endOf("month");
	} else if (moment.isMoment(ccEndDate)) {
		ccEndDate = moment.utc(ccEndDate.format()).endOf("month");
	}

	function isDateRangeIntersecting({ commissionCycleStart, commissionCycleEnd, definitionStart, definitionEnd }) {
		return definitionStart.isSameOrBefore(commissionCycleEnd) && definitionEnd.isSameOrAfter(commissionCycleStart);
	}

	return rows.filter(
		(row) =>
			isDateRangeIntersecting({
				commissionCycleStart: moment.utc(ccStartDate),
				commissionCycleEnd: moment.utc(ccEndDate),
				definitionStart: moment.utc(row.effectiveDateFromItem).startOf("day"),
				definitionEnd: moment.utc(row.effectiveDateToItem).endOf("day"),
			}) || row.isNew
	);
};

const getOriginalCommissionCycleTemp = ({ commissionCycles, mainElement, elementId, listProp, idProp }) => {
	if (!elementId) {
		return {};
	}
	let originalCommissionCycleId = null;
	let parentCommissionCycleId = null;

	const filteredCycles = commissionCycles.filter((cycle) => cycle[listProp].some((setup) => setup[idProp]._id === elementId));

	if (filteredCycles?.length && filteredCycles?.length > 1) {
		const firstElTitle = filteredCycles[0].title;

		// avoiding the first element/parent from the loop
		if (firstElTitle !== mainElement?.title) {
			originalCommissionCycleId = mainElement?._id;
			parentCommissionCycleId = filteredCycles[0]._id;
		}
	}

	return { originalCommissionCycleId, parentCommissionCycleId };
};

export const updateSelectedItemAwards = ({ cachedSidebarData, identifiers, selection, setSelection }) => {
	if (!selection) {
		return;
	}
	const commissionCycleSpiffs = cachedSidebarData?.find((cycle) => cycle._id === selection?._id?.split("_")[0])?.spiffs;
	const itemSelected = commissionCycleSpiffs?.flatMap((spiff) => spiff.items).find((item) => item._id === selection?._id?.split("_")[1]);
	if (!itemSelected) {
		setSelection(null);
	} else {
		if (!itemSelected?.awardsCount?.length) {
			delete itemSelected?.awards;
		}

		const updatedSelection = {
			...itemSelected,
			_id: selection?._id,
			[identifiers.createdOn]: itemSelected?.[identifiers.createdOn],
			[identifiers.lastModifiedBy]: itemSelected?.[identifiers.lastModifiedBy],
			[identifiers.createdBy]: itemSelected?.[identifiers.createdBy],
			[identifiers.lastModifiedOn]: itemSelected?.[identifiers.lastModifiedOn],
		};
		setSelection((prev) => {
			delete prev.awards;
			return { ...prev, ...updatedSelection };
		});
	}
};

export const updateSelectedItem = ({ cachedSidebarData, identifiers, selection, setSelection }) => {
	const commissionCycleSpiffs = cachedSidebarData?.find((cycle) => cycle._id === selection?.commissionCycleId)?.spiffs;
	const itemSelected = commissionCycleSpiffs?.flatMap((spiff) => spiff).find((item) => item._id === selection?._id?.split("_")[1]);

	if (!itemSelected) {
		setSelection(null);
		return;
	}

	const updatedSelection = {
		...itemSelected,
		_id: selection?._id,
		[identifiers.createdAt]: itemSelected?.[identifiers.createdAt],
		[identifiers.lastModifiedBy]: itemSelected?.[identifiers.lastModifiedBy],
		[identifiers.createdBy]: itemSelected?.[identifiers.createdBy],
		[identifiers.lastModifiedOn]: itemSelected?.[identifiers.lastModifiedOn],
	};
	setSelection((prev) => {
		return {
			...prev,
			...updatedSelection,
		};
	});
};

const updateSelection = ({ itemSelected, identifiers, selection }) => {
	return {
		...itemSelected,
		_id: selection?._id,
		[identifiers.createdAt]: itemSelected?.[identifiers.createdAt],
		[identifiers.lastModifiedBy]: itemSelected?.[identifiers.lastModifiedBy],
		[identifiers.createdBy]: itemSelected?.[identifiers.createdBy],
		[identifiers.lastModifiedOn]: itemSelected?.[identifiers.lastModifiedOn],
	};
};

export const updateSelectedItemTemporaryGuaranteed = ({ cachedSidebarData, identifiers, selection, setSelection }) => {
	const commissionCycle = cachedSidebarData?.find((cc) => cc._id === selection?.commissionCycleId)?.temporaryGuaranties;

	const itemSelected = commissionCycle
		?.flatMap((obj) => obj.temporaryGuaranteedId)
		.find((item) => item._id === selection?._id?.split("_")[1]);

	if (!itemSelected) {
		setSelection(null);
		return;
	}

	setSelection((prev) => {
		return {
			...prev,
			...updateSelection({ itemSelected, identifiers, selection }),
		};
	});
};

export const updateSelectedItemSpecialUnits = ({ cachedSidebarData, identifiers, selection, setSelection }) => {
	const commissionCycle = cachedSidebarData?.find((cycle) => cycle._id === selection?.commissionCycleId);

	// Check if the updated selection is outside the commission cycle date range after propagation.
	const isOutOfDateRange =
		moment(selection?.effectiveDateTo).isBefore(commissionCycle?.startDate) ||
		moment(selection?.effectiveDateFrom).isAfter(commissionCycle?.endDate);

	const setupId = selection?.specialUnitsListId || selection?.setupId;

	const itemSelected = commissionCycle?.specialUnitsList?.flatMap((obj) => obj.specialUnitsListId).find((item) => item._id === setupId);

	if (isOutOfDateRange || !itemSelected) {
		setSelection(null);
		return;
	}

	setSelection((prev) => {
		return {
			...prev,
			...updateSelection({ itemSelected, identifiers, selection }),
		};
	});
};

export const updateSelectedItemAutoAlert = ({ cachedSidebarData, identifiers, selection, setSelection }) => {
	const commissionCycleSpiffs = cachedSidebarData?.find((cycle) => cycle._id === selection?.commissionCycleId)?.autoAlertsSetups;

	const itemSelected = commissionCycleSpiffs
		?.flatMap((item) => item.autoAlertsSetupId)
		.find((item) => item._id === selection?._id?.split("_")[1]);

	if (!itemSelected) {
		setSelection(null);
		return;
	}

	const updatedSelection = {
		...itemSelected,
		_id: selection?._id,
		[identifiers.createdAt]: itemSelected?.[identifiers.createdAt],
		[identifiers.lastModifiedBy]: itemSelected?.[identifiers.lastModifiedBy],
		[identifiers.createdBy]: itemSelected?.[identifiers.createdBy],
		[identifiers.lastModifiedOn]: itemSelected?.[identifiers.lastModifiedOn],
	};
	delete updatedSelection.commissionCycleId;

	setSelection((prev) => {
		return {
			...prev,
			...updatedSelection,
		};
	});
};
